import React, { useState } from 'react';
import { 
  Layers, Download, Plus, Save, FileText, Eye,
  ArrowRight, X, Sliders, Calendar, BarChart2, PieChart
} from 'lucide-react';

// Import mock data
import { 
  reportTemplates, 
  reportComponents 
} from '../../data/analyticsData';

const CustomReportBuilder = ({ filters }) => {
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [customReport, setCustomReport] = useState({
    title: 'New Custom Report',
    description: '',
    metrics: [],
    dimensions: [],
    dateRange: 'last30days',
    visualization: 'bar'
  });
  const [previewMode, setPreviewMode] = useState(false);

  // Handle template selection
  const selectTemplate = (template) => {
    setActiveTemplate(template);
    setCustomReport({
      title: template.name,
      description: template.description,
      metrics: template.metrics.slice(0, 3),
      dimensions: template.dimensions.slice(0, 2),
      dateRange: 'last30days',
      visualization: template.visualizations[0]
    });
  };

  // Handle adding metric
  const toggleMetric = (metricId) => {
    if (customReport.metrics.includes(metricId)) {
      setCustomReport({
        ...customReport,
        metrics: customReport.metrics.filter(id => id !== metricId)
      });
    } else {
      setCustomReport({
        ...customReport,
        metrics: [...customReport.metrics, metricId]
      });
    }
  };

  // Handle adding dimension
  const toggleDimension = (dimensionId) => {
    if (customReport.dimensions.includes(dimensionId)) {
      setCustomReport({
        ...customReport,
        dimensions: customReport.dimensions.filter(id => id !== dimensionId)
      });
    } else {
      setCustomReport({
        ...customReport,
        dimensions: [...customReport.dimensions, dimensionId]
      });
    }
  };

  // Get metric or dimension name by ID
  const getMetricName = (id) => {
    return reportComponents.metrics.find(m => m.id === id)?.name || id;
  };

  const getDimensionName = (id) => {
    return reportComponents.dimensions.find(d => d.id === id)?.name || id;
  };

  return (
    <div className="space-y-6">
      {/* Report Builder Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-50 dark:bg-teal-900/20">
              <Layers className="w-5 h-5 text-teal-500 dark:text-teal-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Custom Report Builder</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Create your own custom reports with drag-and-drop simplicity</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setPreviewMode(!previewMode)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium 
                ${previewMode ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' : 
                'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400'}`}
            >
              {previewMode ? <Sliders className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>{previewMode ? 'Edit Report' : 'Preview'}</span>
            </button>
            <button className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>

        {/* Templates */}
        <div className="mb-8">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Start with a template</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {reportTemplates.map((template) => (
              <div 
                key={template.id}
                onClick={() => selectTemplate(template)}
                className={`p-4 rounded-lg cursor-pointer transition-all 
                  ${activeTemplate?.id === template.id ? 
                    'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800' : 
                    'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'} 
                  border`}
              >
                <h5 className="font-medium text-gray-900 dark:text-white">{template.name}</h5>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{template.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {template.visualizations.map((vis) => (
                    <span 
                      key={vis}
                      className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs"
                    >
                      {vis}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {previewMode ? (
          // Preview mode - show placeholder for report visualization
          <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 flex flex-col items-center justify-center">
            <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
              <FileText className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">{customReport.title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mt-2">
              {customReport.description || 'This is a preview of your custom report. In a real implementation, your report data would be visualized here.'}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
                <Download className="w-4 h-4" />
                <span>Export as PDF</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600">
                <Download className="w-4 h-4" />
                <span>Export as Excel</span>
              </button>
            </div>
          </div>
        ) : (
          // Builder mode
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Column - Metrics and Dimensions */}
            <div className="md:col-span-2 space-y-6">
              {/* Report Title and Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Report Title</label>
                <input 
                  type="text" 
                  value={customReport.title}
                  onChange={(e) => setCustomReport({...customReport, title: e.target.value})}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                />
              </div>

              {/* Metrics Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Metrics</label>
                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {customReport.metrics.map((metricId) => (
                      <div 
                        key={metricId}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                      >
                        <span className="text-sm">{getMetricName(metricId)}</span>
                        <button 
                          onClick={() => toggleMetric(metricId)}
                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                        >
                          <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                        </button>
                      </div>
                    ))}
                    {customReport.metrics.length === 0 && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">No metrics selected</div>
                    )}
                  </div>

                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">Available Metrics:</div>
                  <div className="flex flex-wrap gap-2">
                    {reportComponents.metrics.map((metric) => (
                      <button
                        key={metric.id}
                        onClick={() => toggleMetric(metric.id)}
                        disabled={customReport.metrics.includes(metric.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          customReport.metrics.includes(metric.id) ?
                            'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 opacity-50 cursor-not-allowed' :
                            'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-teal-200 dark:hover:border-teal-800'
                        }`}
                      >
                        {metric.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dimensions Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dimensions</label>
                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {customReport.dimensions.map((dimensionId) => (
                      <div 
                        key={dimensionId}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                      >
                        <span className="text-sm">{getDimensionName(dimensionId)}</span>
                        <button 
                          onClick={() => toggleDimension(dimensionId)}
                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                        >
                          <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                        </button>
                      </div>
                    ))}
                    {customReport.dimensions.length === 0 && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">No dimensions selected</div>
                    )}
                  </div>

                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">Available Dimensions:</div>
                  <div className="flex flex-wrap gap-2">
                    {reportComponents.dimensions.map((dimension) => (
                      <button
                        key={dimension.id}
                        onClick={() => toggleDimension(dimension.id)}
                        disabled={customReport.dimensions.includes(dimension.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          customReport.dimensions.includes(dimension.id) ?
                            'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 opacity-50 cursor-not-allowed' :
                            'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-teal-200 dark:hover:border-teal-800'
                        }`}
                      >
                        {dimension.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Visualization and Date Range */}
            <div className="space-y-6">
              {/* Visualization Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Visualization Type</label>
                <div className="space-y-2">
                  {reportComponents.visualizations.map((vis) => (
                    <div 
                      key={vis.id}
                      onClick={() => setCustomReport({...customReport, visualization: vis.id})}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        customReport.visualization === vis.id ?
                          'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800' :
                          'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      } border`}
                    >
                      {vis.id === 'bar' && <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                      {vis.id === 'pie' && <PieChart className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                      {vis.id === 'line' && <TrendingLine className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                      {vis.id === 'table' && <Table className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                      {vis.id !== 'bar' && vis.id !== 'pie' && vis.id !== 'line' && vis.id !== 'table' && 
                        <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      }
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{vis.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</label>
                <div className="space-y-2">
                  {reportComponents.dateRanges.map((range) => (
                    <div 
                      key={range.id}
                      onClick={() => setCustomReport({...customReport, dateRange: range.id})}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        customReport.dateRange === range.id ?
                          'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800' :
                          'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      } border`}
                    >
                      <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{range.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Button */}
              <button 
                onClick={() => setPreviewMode(true)}
                className="w-full flex items-center justify-center gap-2 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
              >
                <Eye className="w-5 h-5" />
                <span>Preview Report</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Help and Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-800/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800/30">
            <Layers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-300">Report Builder Tips</h3>
            <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Start with a template or build your report from scratch by selecting metrics and dimensions.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Choose the right visualization type for your data. Bar charts work well for comparisons, while pie charts show proportions.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Limit your report to 3-5 metrics for better readability and focus.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Save your reports to easily access them later or schedule automatic delivery to your email.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for visualization icons
const TrendingLine = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
    <polyline points="16 7 22 7 22 13"></polyline>
  </svg>
);

const Table = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="3" y1="15" x2="21" y2="15"></line>
    <line x1="9" y1="3" x2="9" y2="21"></line>
    <line x1="15" y1="3" x2="15" y2="21"></line>
  </svg>
);

export default CustomReportBuilder; 